<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <GymTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        :search-fn="globalSearch"
        @select-member="handleSelectMember"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="plans-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('plans.title') }}</h1>
        </div>
        <IntercomButton variant="primary" @click="openCreateModal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('plans.newPlan') }}</span>
        </IntercomButton>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <IntercomSpinner />
      </div>

      <!-- Empty State -->
      <div v-else-if="plans.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <h3>{{ $t('plans.noPlans') }}</h3>
        <IntercomButton variant="primary" @click="openCreateModal">
          {{ $t('plans.newPlan') }}
        </IntercomButton>
      </div>

      <!-- Plans Grid -->
      <div v-else class="plans-grid">
        <IntercomCard
          v-for="plan in plans"
          :key="plan.id"
          class="plan-card"
          :class="{ inactive: !plan.is_active }"
        >
          <div class="plan-header">
            <h3 class="plan-name">{{ plan.name }}</h3>
            <span class="plan-status" :class="{ active: plan.is_active }">
              {{ plan.is_active ? $t('common.active') : $t('common.inactive') }}
            </span>
          </div>

          <div class="plan-price">
            {{ formatCurrency(plan.price) }}
          </div>

          <div class="plan-duration">
            {{ plan.duration_days }} days
          </div>

          <p v-if="plan.description" class="plan-description">
            {{ plan.description }}
          </p>

          <div class="plan-actions">
            <IntercomButton variant="secondary" size="sm" @click="openEditModal(plan)">
              {{ $t('common.edit') }}
            </IntercomButton>
            <IntercomButton
              v-if="plan.is_active"
              variant="ghost"
              size="sm"
              @click="handleDeactivate(plan)"
            >
              {{ $t('common.delete') }}
            </IntercomButton>
          </div>
        </IntercomCard>
      </div>

      <!-- Create/Edit Modal -->
      <IntercomModal v-model:show="showModal" :title="editingPlan ? $t('plans.editPlan') : $t('plans.newPlan')">
        <form @submit.prevent="handleSubmit" class="plan-form">
          <div class="form-field">
            <IntercomInput
              v-model="formData.name"
              :label="$t('plans.name')"
              :placeholder="$t('plans.namePlaceholder')"
              required
            />
          </div>

          <div class="form-row">
            <div class="form-field">
              <IntercomInput
                v-model.number="formData.duration_days"
                :label="$t('plans.duration')"
                type="number"
                min="1"
                required
              />
            </div>
            <div class="form-field">
              <IntercomInput
                v-model.number="formData.price"
                :label="$t('plans.price')"
                type="number"
                min="0"
                required
              />
            </div>
          </div>

          <div class="form-field">
            <label class="field-label">{{ $t('plans.description') }}</label>
            <textarea
              v-model="formData.description"
              :placeholder="$t('plans.descriptionPlaceholder')"
              class="description-input"
              rows="3"
            ></textarea>
          </div>

          <div class="form-row">
            <div class="form-field">
              <IntercomInput
                v-model.number="formData.display_order"
                :label="$t('plans.displayOrder')"
                type="number"
                min="0"
              />
            </div>
            <div class="form-field checkbox-field">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.is_active" />
                <span>{{ $t('plans.isActive') }}</span>
              </label>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <div class="modal-actions">
            <IntercomButton variant="secondary" type="button" @click="closeModal">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton variant="primary" type="submit" :loading="saving">
              {{ editingPlan ? $t('plans.updatePlan') : $t('plans.createPlan') }}
            </IntercomButton>
          </div>
        </form>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useGym } from '../composables/useGym.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomButton, IntercomInput, IntercomSpinner, IntercomModal } from '@shared/components/intercom'
import GymTopbar from '../components/GymTopbar.vue'

const router = useRouter()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const { listPlans, createPlan, updatePlan, deletePlan, globalSearch } = useGym()

const { navSections } = useNavigation()

// State
const loading = ref(true)
const saving = ref(false)
const plans = ref([])
const showModal = ref(false)
const editingPlan = ref(null)
const errorMessage = ref('')

const formData = reactive({
  name: '',
  duration_days: 30,
  price: 1000,
  description: '',
  display_order: 0,
  is_active: true,
})

// Computed
const businessName = computed(() => user.value?.business?.name || '')

// Methods
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

const fetchPlans = async () => {
  try {
    loading.value = true
    const response = await listPlans()
    plans.value = response.plans || []
  } catch (error) {
    console.error('Failed to fetch plans:', error)
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingPlan.value = null
  formData.name = ''
  formData.duration_days = 30
  formData.price = 1000
  formData.description = ''
  formData.display_order = plans.value.length
  formData.is_active = true
  errorMessage.value = ''
  showModal.value = true
}

const openEditModal = (plan) => {
  editingPlan.value = plan
  formData.name = plan.name
  formData.duration_days = plan.duration_days
  formData.price = plan.price
  formData.description = plan.description || ''
  formData.display_order = plan.display_order || 0
  formData.is_active = plan.is_active
  errorMessage.value = ''
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingPlan.value = null
}

const handleSubmit = async () => {
  try {
    saving.value = true
    errorMessage.value = ''

    const data = {
      name: formData.name,
      duration_days: formData.duration_days,
      price: formData.price,
      description: formData.description,
      display_order: formData.display_order,
      is_active: formData.is_active,
    }

    if (editingPlan.value) {
      await updatePlan(editingPlan.value.id, data)
    } else {
      await createPlan(data)
    }

    closeModal()
    await fetchPlans()
  } catch (error) {
    console.error('Failed to save plan:', error)
    errorMessage.value = error.message || t('errors.generic')
  } finally {
    saving.value = false
  }
}

const handleDeactivate = async (plan) => {
  if (!confirm(t('plans.deleteConfirm'))) return

  try {
    await deletePlan(plan.id)
    await fetchPlans()
  } catch (error) {
    console.error('Failed to deactivate plan:', error)
  }
}

const handleSelectMember = (member) => {
  router.push(`/members/${member.id}`)
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchPlans()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.plans-view {
  padding: var(--space-6);
  max-width: 1200px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-12);
}

.empty-state {
  text-align: center;
  padding: var(--space-12);
}

.empty-icon {
  color: var(--color-gray-300);
  margin-bottom: var(--space-4);
}

.empty-state h3 {
  font-size: var(--font-size-lg);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-6) 0;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--space-4);
}

.plan-card {
  padding: var(--space-5);
  transition: box-shadow 0.2s;
}

.plan-card:hover {
  box-shadow: var(--shadow-md);
}

.plan-card.inactive {
  opacity: 0.6;
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-3);
}

.plan-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

.plan-status {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  background: var(--color-gray-100);
  color: var(--color-gray-500);
}

.plan-status.active {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.plan-price {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-blue);
  margin-bottom: var(--space-1);
}

.plan-duration {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-3);
}

.plan-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  margin: 0 0 var(--space-4) 0;
  line-height: 1.5;
}

.plan-actions {
  display: flex;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-gray-100);
}

/* Modal Form */
.plan-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.description-input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-family: inherit;
  resize: vertical;
}

.description-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-field {
  display: flex;
  align-items: flex-end;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

.checkbox-label input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.error-message {
  padding: var(--space-3) var(--space-4);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

@media (max-width: 768px) {
  .plans-view {
    padding: var(--space-4);
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
